import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { RefreshCw, X } from "lucide-react";
import { Button } from "@/components/ui/button";

// Karta „Je k dispozici nová verze" (zadání 155). Porovnává id sestavení v bundlu
// s public/version.json (network-first SW + no-store + ?t= → vždy z serveru).
// Nic nereloaduje samo — dělník může být uprostřed odchodu s fotkami.
// DefinePlugin dosadí id při buildu; čte se při renderu (ne při importu), aby šel modul testovat.
const currentBuild = () => process.env.REACT_APP_BUILD_ID;
const FIRST_DELAY = 15_000;
const EVERY = 10 * 60_000;
const ROUTE_DEBOUNCE = 30_000;
const DISMISS_KEY = "update_dismissed";

export function UpdateAvailable() {
  const CURRENT = currentBuild();
  const active = !!CURRENT && CURRENT !== "dev";
  const [newBuild, setNewBuild] = useState(null);
  const [dismissedFor, setDismissedFor] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) || null; } catch { return null; }
  });
  const location = useLocation();
  const lastRouteCheck = useRef(0);

  const check = useCallback(async () => {
    if (!active) return;
    try {
      const r = await fetch("/version.json?t=" + Date.now(), { cache: "no-store" });
      const json = await r.json();
      if (json?.build && json.build !== CURRENT) setNewBuild(json);
    } catch { /* offline / chyba sítě — zkusíme příště */ }
  }, [active, CURRENT]);

  useEffect(() => {
    if (!active) return undefined;
    const first = setTimeout(check, FIRST_DELAY);
    const every = setInterval(check, EVERY);
    const onVisible = () => { if (document.visibilityState === "visible") check(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", check);
    return () => {
      clearTimeout(first); clearInterval(every);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", check);
    };
  }, [active, check]);

  useEffect(() => {
    if (!active) return;
    const now = Date.now();
    if (now - lastRouteCheck.current < ROUTE_DEBOUNCE) return;
    lastRouteCheck.current = now;
    check();
  }, [location.pathname, active, check]);

  if (!active || !newBuild || newBuild.build === dismissedFor) return null;

  const dismiss = () => {
    setDismissedFor(newBuild.build);
    try { sessionStorage.setItem(DISMISS_KEY, newBuild.build); } catch { /* private mode */ }
  };

  return (
    <div role="status" data-testid="update-available"
      className="fixed z-40 left-3 right-3 md:left-6 md:right-auto md:w-[340px] bottom-[calc(var(--bottom-nav-h,5rem)+10px+env(safe-area-inset-bottom))] md:bottom-6 bg-card border border-border rounded-[16px] shadow-lg p-3.5 flex items-start gap-3 motion-safe:animate-in motion-safe:slide-in-from-bottom-2 motion-safe:fade-in">
      <span className="mt-0.5 shrink-0 h-9 w-9 rounded-full bg-brand/10 text-brand grid place-items-center"><RefreshCw size={18} /></span>
      <div className="min-w-0 flex-1">
        <p className="font-bold text-sm">Je k dispozici nová verze Proyekty</p>
        <p className="text-xs text-muted-foreground mt-0.5">Aktualizace trvá vteřinu. Rozepsaný text si nejdřív uložte.</p>
        <Button size="sm" className="mt-2.5 h-9 w-full md:w-auto font-bold" onClick={() => window.location.reload()} data-testid="update-reload">Aktualizovat</Button>
      </div>
      <button type="button" aria-label="Zavřít" onClick={dismiss} className="text-muted-foreground hover:text-foreground p-1 -m-1" data-testid="update-dismiss"><X size={16} /></button>
    </div>
  );
}

// Řádek „Verze aplikace …" pro Nastavení a patičku draweru (155).
export function AppVersionLine({ className = "text-xs text-muted-foreground", testid = "app-version" }) {
  const build = process.env.REACT_APP_BUILD_ID || "dev";
  const at = process.env.REACT_APP_BUILT_AT;
  const d = at ? new Date(at) : null;
  const when = d && !isNaN(d)
    ? `${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`
    : "—";
  return <p className={className} data-testid={testid}>Verze aplikace {when} · {build}</p>;
}
